import { type FC, memo, useEffect, useMemo, useState, type JSX } from 'react';
import { twMerge } from 'tailwind-merge';
import { Container } from 'Container';
import { useSuspenseFeatureGate } from 'FrontRoyalStatsig';
import { FeaturedStudents } from 'FeaturedStudents';
import { useCurrentUserIguanaObject } from 'FrontRoyalAngular';
import { type CurrentUserIguanaObject, getCohort, getProgramInclusion } from 'Users';
import { type AnyObject } from '@Types';
import { EventList } from 'EventList';
import { navigationHelper, useAppHeaderViewModel } from 'NavigationHelpers';
import { ProgramTypeConfigs } from 'Program';
import { StudentSpotlightCarousel, type StudentSpotlightCarouselProps } from 'StudentSpotlight';
import { useStudentDashboardContent } from 'LearnerContentCache';
import { SupportSidebar as SupportSidebarComponent } from 'Support/SupportSideBar';
import clsx from 'clsx';
import { useLearningBoxMode } from 'NavigationHelpers/useLearningBoxMode';
import { Footer as FooterComponent } from 'Footer';
import { MeetYourClass } from 'MeetYourClass';
import { useAppHeaderHasTopMessage } from 'NavigationHelpers/useAppHeaderHasTopMessage';
import { BookmarkedAndCompletedCourses } from 'BookmarkedAndCompletedCourses';
import { useSiteMetadata } from 'SearchEngineOptimization/useSiteMetadata';
import { useContentProgress } from 'ContentProgress';
import {
    TopMessageV1,
    BookmarkedCompletedCoursesv1,
    SidebarV1,
    FeaturedEventsV1,
    LearningBoxV1,
} from './studentDashboardNgDirectives';
import { onFeaturedEventsLoaded } from './onFeaturedEventsLoaded';
import { Placeholder } from './Placeholder';
import {
    DesktopCourseware as DesktopCoursewareComponent,
    MobileCourseware as MobileCoursewareComponent,
} from './Courseware';

function BookmarkedCompletedCoursesGate() {
    const bookmarkedCoursesVersion = useSuspenseFeatureGate('sd_bookmarked_courses_v2') ? 2 : 1;
    const learningBoxMode = useLearningBoxMode();

    return bookmarkedCoursesVersion === 1 ? (
        <div className={clsx(learningBoxMode === 'playlists' && 'hidden sm:block')}>
            <BookmarkedCompletedCoursesv1 />
        </div>
    ) : (
        <BookmarkedAndCompletedCourses />
    );
}

function FeaturedStudentsGate({
    studentNetworkActivated,
    studentSpotlightVersion,
    meetClassVersion,
    eventsVersion,
    studentSpotlightCarouselProps,
}: {
    studentNetworkActivated: boolean;
    studentSpotlightVersion: number;
    meetClassVersion: number;
    eventsVersion: number;
    studentSpotlightCarouselProps: StudentSpotlightCarouselProps;
}) {
    const { loadRoute } = navigationHelper();

    if (meetClassVersion === 2 && studentNetworkActivated) {
        return <MeetYourClass />;
    }
    if (studentSpotlightVersion === 2 && !studentNetworkActivated) {
        return <StudentSpotlightCarousel {...studentSpotlightCarouselProps} />;
    }

    return (
        <div className={twMerge(eventsVersion === 1 ? 'order-2' : '')}>
            <FeaturedStudents inStudentNetwork={studentNetworkActivated} loadRoute={loadRoute} />
        </div>
    );
}

function EventsGate({
    currentUser,
    studentNetworkActivated,
    showFeaturedEvents,
    setShowFeaturedEvents,
    eventsVersion,
}: {
    currentUser: CurrentUserIguanaObject;
    studentNetworkActivated: boolean;
    showFeaturedEvents: boolean;
    setShowFeaturedEvents: (showFeaturedEvents: boolean) => void;
    eventsVersion: number;
}) {
    return eventsVersion === 1 ? (
        showFeaturedEvents && (
            <div className="order-1">
                <FeaturedEventsV1
                    timezone={currentUser.timezone}
                    studentNetworkActivated={studentNetworkActivated}
                    onEventsLoaded={(orderedEventTitles: string[]) => {
                        onFeaturedEventsLoaded(orderedEventTitles, studentNetworkActivated, currentUser);

                        // `currentUser.showFeaturedEvents` can be explicitly set to false in `onFeaturedEventsLoaded`.
                        if ((currentUser as AnyObject).showFeaturedEvents === false) {
                            setShowFeaturedEvents(false);
                        }
                    }}
                />
            </div>
        )
    ) : (
        <EventList featured={!studentNetworkActivated} />
    );
}

function FeaturedStudentsAndEventsGate() {
    const studentSpotlightVersion = useSuspenseFeatureGate('sd_student_spotlight_and_events_v2') ? 2 : 1;
    const meetClassVersion = useSuspenseFeatureGate('sd_meet_class_and_events_v2') ? 2 : 1;
    const currentUser = useCurrentUserIguanaObject();
    const [showFeaturedEvents, setShowFeaturedEvents] = useState(false);
    const studentNetworkActivated = useMemo(
        () => !!getProgramInclusion(currentUser)?.studentNetworkActivated,
        [currentUser],
    );
    const cohort = useMemo(() => getCohort(currentUser), [currentUser]);
    const programTypeConfig = cohort && ProgramTypeConfigs[cohort.programType];

    const eventsVersion =
        (studentSpotlightVersion === 2 && !studentNetworkActivated) ||
        (meetClassVersion === 2 && studentNetworkActivated)
            ? 2
            : 1;

    useEffect(() => {
        if (currentUser) {
            setShowFeaturedEvents((currentUser as AnyObject).showFeaturedEvents !== false);
        } else {
            setShowFeaturedEvents(false);
        }
    }, [currentUser]);

    if (!currentUser || !cohort?.providesStudentNetworkAccess) return null;

    return (
        // We hide the v1 featured students and events boxes on mobile, but show them on desktop.
        // The v2 boxes are always visible, both on desktop and on mobile.
        <div className={twMerge('gap-7.5', eventsVersion === 1 ? 'hidden grid-cols-2 md:grid' : 'flex flex-col')}>
            <FeaturedStudentsGate
                studentNetworkActivated={studentNetworkActivated}
                studentSpotlightVersion={studentSpotlightVersion}
                meetClassVersion={meetClassVersion}
                eventsVersion={eventsVersion}
                studentSpotlightCarouselProps={programTypeConfig?.studentSpotlightCarouselProps ?? { streamIds: [] }}
            />
            <EventsGate
                currentUser={currentUser}
                studentNetworkActivated={studentNetworkActivated}
                showFeaturedEvents={showFeaturedEvents}
                setShowFeaturedEvents={setShowFeaturedEvents}
                eventsVersion={eventsVersion}
            />
        </div>
    );
}

function SidebarGate() {
    const sidebarVersion = useSuspenseFeatureGate('sd_sidebar_v2') ? 2 : 1;

    return sidebarVersion === 1 ? (
        <SidebarV1 />
    ) : (
        <Placeholder className="w-full max-w-[350px] sm:hidden">Sidebar</Placeholder>
    );
}

// Courseware v1 handles the desktop and mobile courseware.
// In Courseware v2, we only render the desktop courseware here,
// and the mobile courseware is rendered in the MobileCoursewareGate. (see below)
function DesktopAndLegacyMobileCoursewareGate() {
    const coursewareVersion = useSuspenseFeatureGate('sd_courseware_v2') ? 2 : 1;

    const hasTopMessage = useAppHeaderHasTopMessage();
    const studentDashboardContent = useStudentDashboardContent();
    const contentProgress = useContentProgress();

    return coursewareVersion === 1 ? (
        <>
            <div
                className={clsx('invisible sm:visible', {
                    '-mb-3.5 sm:-mb-7.5': !hasTopMessage,
                    'mb-0': hasTopMessage,
                })}
            >
                <TopMessageV1 />
            </div>
            <LearningBoxV1 studentDashboardContent={studentDashboardContent} contentProgress={contentProgress} />
        </>
    ) : (
        <DesktopCoursewareComponent />
    );
}

function MobileCoursewareGate() {
    const coursewareVersion = useSuspenseFeatureGate('sd_courseware_v2') ? 2 : 1;

    return coursewareVersion === 2 ? <MobileCoursewareComponent /> : null;
}

function FooterGate() {
    const coursewareVersion = useSuspenseFeatureGate('sd_courseware_v2') ? 2 : 1;
    const learningBoxMode = useLearningBoxMode();

    return coursewareVersion === 2 ? (
        <FooterComponent />
    ) : (
        <div className={clsx(learningBoxMode === 'playlists' && 'hidden sm:block')}>
            <FooterComponent />
        </div>
    );
}

function SupportSidebarGate() {
    const limitedSupportModal = useSuspenseFeatureGate('limited_support_modal');
    return limitedSupportModal ? null : <SupportSidebarComponent className="hidden md:block" />;
}

type StudentDashboardPresentationProps = {
    SupportSidebar: JSX.Element;
    MobileCourseware: JSX.Element;
    DesktopCourseware: JSX.Element;
    FeaturedStudentsAndEvents: JSX.Element;
    Sidebar: JSX.Element;
    BookmarkedCompletedCourses: JSX.Element;
    Footer: JSX.Element;
};

export const StudentDashboardPresentation: FC<StudentDashboardPresentationProps> = ({
    SupportSidebar,
    MobileCourseware,
    DesktopCourseware,
    FeaturedStudentsAndEvents,
    Sidebar,
    BookmarkedCompletedCourses,
    Footer,
}) => (
    <div className="student-dashboard fade-in bg-slate-grey-100-alt sm:bg-beige">
        {MobileCourseware}
        <Container className="pb-9 sm:pb-14">
            {SupportSidebar}
            <div className="mx-auto flex flex-col gap-3.5 sm:pt-7.5">
                {/* First Row */}
                <div className="flex flex-col justify-between gap-3.5 md:flex-row md:gap-7.5">
                    {/* Main Content */}
                    <div className="main-column flex w-full shrink-0 flex-col gap-3.5 md:w-2/3 md:grow-0 md:gap-7.5">
                        {DesktopCourseware}
                        {FeaturedStudentsAndEvents}
                    </div>
                    {Sidebar}
                </div>

                {/* Second Row */}
                <div className="bookmarked-courses-container w-full">{BookmarkedCompletedCourses}</div>
            </div>
        </Container>
        {Footer}
    </div>
);

const StudentDashboardComponent: FC = () => {
    const AppHeaderViewModel = useAppHeaderViewModel();
    const SiteMetadata = useSiteMetadata();

    useEffect(() => {
        if (AppHeaderViewModel.showAlternateHomeButton) {
            AppHeaderViewModel.showAlternateHomeButton = false;
        }
        if (AppHeaderViewModel.showCaret) {
            AppHeaderViewModel.showCaret = false;
        }
    }, [AppHeaderViewModel]);

    useEffect(() => {
        SiteMetadata.updateHeaderMetadata();
    }, [SiteMetadata]);

    return (
        <StudentDashboardPresentation
            MobileCourseware={<MobileCoursewareGate />}
            DesktopCourseware={<DesktopAndLegacyMobileCoursewareGate />}
            SupportSidebar={<SupportSidebarGate />}
            FeaturedStudentsAndEvents={<FeaturedStudentsAndEventsGate />}
            Sidebar={<SidebarGate />}
            BookmarkedCompletedCourses={<BookmarkedCompletedCoursesGate />}
            Footer={<FooterGate />}
        />
    );
};

export const StudentDashboard = memo(StudentDashboardComponent) as typeof StudentDashboardComponent;
